import { createElement, useMemo, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type RichNode = string | { tag: string; children: RichNode[]; href?: string }

const allowedTags = new Set([
  'strong', 'em', 'u', 'del', 'code', 'pre', 'a', 'br', 'p', 'div', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'hr',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
])
const discardedTags = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'form', 'input',
  'textarea', 'button', 'img', 'video', 'audio', 'source', 'link', 'meta', 'base',
  'template', 'noscript',
])
const blockTags = new Set([
  'pre', 'p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol',
  'blockquote', 'hr', 'table',
])
const tagAliases: Record<string, string> = { b: 'strong', i: 'em', s: 'del', strike: 'del' }

function safeLink(value: string): string | undefined {
  try {
    const url = new URL(value)
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined
  } catch {
    return undefined
  }
}

function readNodes(parent: Node): RichNode[] {
  return Array.from(parent.childNodes).flatMap((node): RichNode[] => {
    if (node.nodeType === Node.TEXT_NODE) return [node.textContent || '']
    if (node.nodeType !== Node.ELEMENT_NODE) return []
    const element = node as Element
    const name = element.tagName.toLowerCase()
    if (discardedTags.has(name)) return []
    const tag = tagAliases[name] || name
    const children = readNodes(node)
    if (!allowedTags.has(tag)) return children
    return [{ tag, children, ...(tag === 'a' ? { href: safeLink(element.getAttribute('href') || '') } : {}) }]
  })
}

function textOf(nodes: RichNode[]): string {
  return nodes.map((node) => typeof node === 'string' ? node : textOf(node.children)).join('')
}

// Older research uses a small Markdown subset alongside Telegram HTML.
function inlineMarkdown(text: string): ReactNode[] {
  const pattern = /(`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|\[[^\]\n]+\]\([^\s)]+\)|\*[^*\n]+\*)/g
  const result: ReactNode[] = []
  let end = 0
  for (const match of text.matchAll(pattern)) {
    result.push(text.slice(end, match.index))
    const token = match[0]
    const key = match.index
    if (token.startsWith('`')) result.push(<code key={key}>{token.slice(1, -1)}</code>)
    else if (token.startsWith('**') || token.startsWith('__')) result.push(<strong key={key}>{token.slice(2, -2)}</strong>)
    else if (token.startsWith('*')) result.push(<em key={key}>{token.slice(1, -1)}</em>)
    else {
      const [, label, target] = token.match(/^\[([^\]]+)\]\((.+)\)$/)!
      const href = safeLink(target)
      result.push(href ? <a key={key} href={href} target="_blank" rel="noopener noreferrer">{label}</a> : label)
    }
    end = match.index + token.length
  }
  result.push(text.slice(end))
  return result
}

function renderNodes(nodes: RichNode[], literal = false): ReactNode[] {
  return nodes.map((node, key) => {
    if (typeof node === 'string') return literal ? node : inlineMarkdown(node)
    const children = renderNodes(node.children, literal || node.tag === 'code' || node.tag === 'pre')
    if (node.tag === 'a' && !node.href) return <span key={key}>{children}</span>
    const props = node.tag === 'a'
      ? { key, href: node.href, target: '_blank', rel: 'noopener noreferrer' }
      : { key }
    return createElement(node.tag, props, ...children)
  })
}

function stripPrefix(nodes: RichNode[], pattern: RegExp): RichNode[] {
  const [first, ...rest] = nodes
  if (typeof first === 'string') return [first.replace(pattern, ''), ...rest]
  return nodes
}

function renderLines(lines: RichNode[][]): ReactNode[] {
  const blocks: ReactNode[] = []
  let paragraph: RichNode[][] = []
  let list: RichNode[][] = []
  const flush = () => {
    if (paragraph.length) {
      blocks.push(<p key={blocks.length}>{renderNodes(paragraph.flatMap((line, i) => i ? ['\n', ...line] : line))}</p>)
      paragraph = []
    }
    if (list.length) {
      blocks.push(<ul key={blocks.length}>{list.map((line, i) => <li key={i}>{renderNodes(line)}</li>)}</ul>)
      list = []
    }
  }
  const cells = (text: string) => text.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim())
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]
    const text = textOf(line).trim()
    if (!text) { flush(); continue }

    const next = textOf(lines[index + 1] || [])
    if (text.includes('|') && next.includes('|') && cells(next).every((cell) => /^:?-{3,}:?$/.test(cell))) {
      flush()
      const rows: string[][] = []
      index += 2
      for (; index < lines.length && textOf(lines[index]).includes('|'); index += 1) rows.push(cells(textOf(lines[index])))
      index -= 1
      blocks.push(
        <div key={blocks.length} className="overflow-x-auto">
          <table><thead><tr>{cells(text).map((cell, i) => <th key={i}>{inlineMarkdown(cell)}</th>)}</tr></thead>
            <tbody>{rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{inlineMarkdown(cell)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      )
      continue
    }
    const meaningful = line.filter((node) => typeof node !== 'string' || node.trim())
    const boldHeading = meaningful.length === 1 && typeof meaningful[0] !== 'string' && meaningful[0].tag === 'strong'
    if (boldHeading || /^#{1,6}\s/.test(text) || /^\*\*[^*]+\*\*$/.test(text)) {
      flush()
      blocks.push(<h4 key={blocks.length}>{renderNodes(stripPrefix(line, /^\s*#{1,6}\s+/))}</h4>)
    } else if (/^\s*[•▸*-]\s+/.test(text)) {
      if (paragraph.length) flush()
      list.push(stripPrefix(line, /^\s*[•▸*-]\s+/))
    } else {
      if (list.length) flush()
      paragraph.push(line)
    }
  }
  flush()
  return blocks
}

function formatContent(nodes: RichNode[]): ReactNode[] {
  const blocks: ReactNode[] = []
  let lines: RichNode[][] = [[]]
  const flush = () => {
    blocks.push(...renderLines(lines).map((block, i) => <div key={`${blocks.length}-${i}`}>{block}</div>))
    lines = [[]]
  }
  for (const node of nodes) {
    if (typeof node === 'string') {
      node.split(/\r?\n/).forEach((text, index) => {
        if (index) lines.push([])
        lines[lines.length - 1].push(text)
      })
    } else if (node.tag === 'br') lines.push([])
    else if (blockTags.has(node.tag)) {
      flush()
      blocks.push(<div key={blocks.length}>{renderNodes([node])}</div>)
    } else lines[lines.length - 1].push(node)
  }
  flush()
  return blocks
}

export function ResearchContent({ content, className }: { content: string; className?: string }) {
  const blocks = useMemo(() => {
    // Template content stays inert. Rebuild only approved elements as React nodes;
    // never insert model output, event attributes, or embedded media into the page.
    const template = document.createElement('template')
    template.innerHTML = content
    return formatContent(readNodes(template.content))
  }, [content])

  return (
    <div className={cn(
      'space-y-3 text-sm leading-7 text-foreground/90 [overflow-wrap:anywhere]',
      '[&_p]:whitespace-pre-wrap [&_strong]:font-semibold [&_strong]:text-foreground',
      '[&_h1]:text-base [&_h2]:text-base [&_h3]:text-base [&_h4]:text-sm',
      '[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold',
      '[&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_h4]:text-foreground',
      '[&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5',
      '[&_code]:rounded [&_code]:bg-muted/60 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs [&_code]:break-all',
      '[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted/40 [&_pre]:p-3 [&_pre]:whitespace-pre-wrap',
      '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:opacity-80',
      '[&_blockquote]:border-l-2 [&_blockquote]:border-primary/40 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground',
      '[&_table]:w-full [&_table]:text-left [&_table]:text-xs [&_th]:border-b [&_th]:p-2 [&_td]:border-b [&_td]:p-2',
      className,
    )}>{blocks}</div>
  )
}
